import React, { useState } from 'react';
import { GitBranch, Search, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';

const GitCheatsheet = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Todos' },
    { id: 'basic', name: 'Básicos' },
    { id: 'branch', name: 'Branches' },
    { id: 'commit', name: 'Commits' },
    { id: 'merge', name: 'Merge & Rebase' },
    { id: 'remote', name: 'Remote' },
    { id: 'stash', name: 'Stash' },
    { id: 'log', name: 'Log & History' }
  ];

  const commands = [
    {
      category: 'basic',
      command: 'git init',
      description: 'Inicializa um repositório Git',
      example: 'git init'
    },
    {
      category: 'basic',
      command: 'git clone',
      description: 'Clona um repositório remoto',
      example: 'git clone https://github.com/user/repo.git'
    },
    {
      category: 'basic',
      command: 'git add',
      description: 'Adiciona arquivos ao staging',
      example: 'git add .'
    },
    {
      category: 'basic',
      command: 'git status',
      description: 'Mostra o status do repositório',
      example: 'git status'
    },
    {
      category: 'commit',
      command: 'git commit',
      description: 'Cria um commit com as mudanças',
      example: 'git commit -m "feat: add new feature"'
    },
    {
      category: 'commit',
      command: 'git commit --amend',
      description: 'Modifica o último commit',
      example: 'git commit --amend -m "new message"'
    },
    {
      category: 'branch',
      command: 'git branch',
      description: 'Lista todas as branches',
      example: 'git branch'
    },
    {
      category: 'branch',
      command: 'git checkout',
      description: 'Muda para uma branch',
      example: 'git checkout feature-branch'
    },
    {
      category: 'branch',
      command: 'git checkout -b',
      description: 'Cria e muda para nova branch',
      example: 'git checkout -b new-feature'
    },
    {
      category: 'merge',
      command: 'git merge',
      description: 'Mescla uma branch na atual',
      example: 'git merge feature-branch'
    },
    {
      category: 'merge',
      command: 'git rebase',
      description: 'Reaplica commits em nova base',
      example: 'git rebase main'
    },
    {
      category: 'remote',
      command: 'git remote add',
      description: 'Adiciona um repositório remoto',
      example: 'git remote add origin https://github.com/user/repo.git'
    },
    {
      category: 'remote',
      command: 'git push',
      description: 'Envia commits para o remoto',
      example: 'git push origin main'
    },
    {
      category: 'remote',
      command: 'git pull',
      description: 'Baixa e mescla mudanças do remoto',
      example: 'git pull origin main'
    },
    {
      category: 'stash',
      command: 'git stash',
      description: 'Salva mudanças temporariamente',
      example: 'git stash'
    },
    {
      category: 'stash',
      command: 'git stash pop',
      description: 'Aplica o último stash',
      example: 'git stash pop'
    },
    {
      category: 'log',
      command: 'git log',
      description: 'Mostra histórico de commits',
      example: 'git log --oneline'
    },
    {
      category: 'log',
      command: 'git diff',
      description: 'Mostra diferenças entre commits',
      example: 'git diff HEAD~1'
    }
  ];

  const filteredCommands = commands.filter(cmd => {
    const matchesSearch = cmd.command.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cmd.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || cmd.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Comando copiado!');
  };

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center gap-2 mb-6">
        <GitBranch className="w-6 h-6 text-gray-700" />
        <h1 className="text-2xl font-bold text-gray-900">Git Cheatsheet</h1>
      </div>
      <p className="text-gray-700 mb-6">Comandos Git essenciais organizados por categoria. Clique no ícone de copiar para copiar o comando.</p>

      {/* Filtros */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar comandos..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de comandos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCommands.map((cmd, index) => (
          <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-mono text-sm font-semibold text-gray-900 break-all">
                {cmd.command}
              </h3>
              <button
                onClick={() => copyToClipboard(cmd.example)}
                className="ml-2 text-gray-400 hover:text-blue-600 transition-colors"
                title="Copiar comando"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-2">{cmd.description}</p>
            <div className="bg-gray-100 rounded p-2">
              <code className="text-xs text-gray-800 font-mono break-all">
                {cmd.example}
              </code>
            </div>
          </div>
        ))}
      </div>

      {filteredCommands.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          Nenhum comando encontrado com os filtros atuais.
        </div>
      )}
    </div>
  );
};

export default GitCheatsheet; 